import React, { useState, useEffect } from 'react';
import { Button, Input, Popconfirm, Space, Table, Typography } from 'antd';
import { connect } from 'react-redux';
import { RootState } from '@store/index';
import { getDirectors } from '@store/Actions/GetDirectors';
import { deleteDirector } from '@store/Actions/DeleteDirector';
import { DeleteTwoTone, EditTwoTone, SearchOutlined } from '@ant-design/icons';
import UpdateDirector from '@components/UpdateDirector';
import Cookies from 'js-cookie';

interface Props {
    getDirectors: () => void;
    directors: IDirectors[];
    loading: boolean;
    deleteDirector: (
        userId: number,
        email: string,
        name: string,
        lastName: string,
        photo: string,
        phoneNumber: string,
        isActive: number,
        companyId: number,
    ) => void;
}

export interface IDirectors {
    companyId: number;
    email: string;
    franchise: string;
    franchiseId: number;
    isActive: number;
    isDirector: false;
    lastName: string;
    name: string;
    phoneNumber: string;
    photo: string;
    userId: number;
    nameComplete: string;
}

interface userNames {
    text: string;
    value: string;
}

interface nameFranchises {
    text: string;
    value: string;
}

const DirectorTable: React.FC<Props> = ({
    getDirectors,
    directors,
    loading,
    deleteDirector,
}): JSX.Element => {
    const data: IDirectors[] = [];
    const companyId = Cookies.get('companyId');

    const [modalVisible, setModalVisible] = useState<boolean>(false);
    const [rowData, setRowData] = useState<any>('');

    const [directorNames, setDirectorsNames] = useState<userNames[]>([]);
    const [nameFranchise, setNameFranchise] = useState<nameFranchises[]>([]);
    const [searchText, setSearchText] = useState<string>('');
    const [searchColumn, setSearchColumn] = useState<string>('');

    directors.map((manager: IDirectors) => {
        if (manager.isActive === 1) {
            data.push({
                ...manager,
                nameComplete: `${manager.name} ${manager.lastName}`,
            });
        }
    });

    useEffect(() => {
        getDirectors();
    }, []);

    useEffect(() => {
        const directorsNames: any[] = [];
        const franchiseName: any[] = [];
        data.map((element: IDirectors) => {
            franchiseName.push(element.franchise);
            directorsNames.push({ text: element.name, value: element.name });
        });

        setDirectorsNames(directorsNames);
        filterUniqueFranchise(franchiseName);
    }, [directors]);

    const filterUniqueFranchise = (franchise: string[]) => {
        const uniqueFranchise: any[] = [];
        const unique = franchise.filter((c, index) => {
            return franchise.indexOf(c) === index;
        });

        unique.forEach((element) => {
            uniqueFranchise.push({ text: element, value: element });
        });
        setNameFranchise(uniqueFranchise);
    };

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const closeModal = () => {
        setModalVisible(false);
    };

    const getColumnSearch = (dataIndex: string, textString: string) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
        }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Escribe aqui el ${textString}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() =>
                        handleSearch(selectedKeys, confirm, dataIndex)
                    }
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() =>
                            handleSearch(selectedKeys, confirm, dataIndex)
                        }
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Buscar
                    </Button>
                    <Button
                        onClick={() => handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Limpiar
                    </Button>
                </Space>
            </div>
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex]
                      .toString()
                      .toLowerCase()
                      .includes(value.toLowerCase())
                : '',
    });

    const deleteRow = async (director: any) => {
        await deleteDirector(
            director.userId,
            director.email,
            director.name,
            director.lastName,
            director.photo,
            director.phoneNumber,
            0, //isActive
            parseInt(companyId ?? ''),
        );
        getDirectors();
    };

    const columns = [
        {
            title: 'Nombre',
            dataIndex: 'nameComplete',
            width: '35%',
            editable: true,
            filters: directorNames,
            ...getColumnSearch('nameComplete', 'director'),
            filterIcon: (filtered) => (
                <SearchOutlined
                    style={{ color: filtered ? '#1890ff' : undefined }}
                />
            ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            width: '30%',
            editable: true,
            ...getColumnSearch('email', 'correo'),
            filterIcon: (filtered) => (
                <SearchOutlined
                    style={{ color: filtered ? '#1890ff' : undefined }}
                />
            ),
        },
        {
            title: 'Teléfono',
            dataIndex: 'phoneNumber',
            width: '30%',
            editable: true,
            ...getColumnSearch('phoneNumber', 'telefono'),
            filterIcon: (filtered) => (
                <SearchOutlined
                    style={{ color: filtered ? '#1890ff' : undefined }}
                />
            ),
        },
        {
            title: '',
            width: '5%',
            dataIndex: 'operation',
            render: (_: any, record: IDirectors) => {
                return (
                    <div className="actions__icon">
                        <Typography.Link
                            onClick={() => {
                                setModalVisible(true);
                                setRowData(record);
                            }}
                        >
                            <EditTwoTone />
                        </Typography.Link>
                        <Typography.Link>
                            <Popconfirm
                                title="¿Eliminar usuario?"
                                okText="Ok"
                                cancelText="Cancelar"
                                onConfirm={() => deleteRow(record)}
                            >
                                <DeleteTwoTone />
                            </Popconfirm>
                        </Typography.Link>
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <Table
                dataSource={data}
                columns={columns}
                size="small"
                loading={loading}
            ></Table>
            <UpdateDirector
                modalVisible={modalVisible}
                closeModal={closeModal}
                rowData={rowData}
            />
        </>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        directors: state.GetDirectors.directors,
        loading: state.GetDirectors.loading,
    };
};

const mapDispatchToProps = {
    getDirectors,
    deleteDirector,
};

export default connect(mapStateToProps, mapDispatchToProps)(DirectorTable);
